import React, { type ReactNode } from 'react';
import type { JqlUtils as JqlUtilsType } from '@atlassian/jira-jql-utils/src/main.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createContainer,
	createHook,
	type Action,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { SortDirection } from '../../common/types.tsx';

type StoreType = {
	field: string | undefined;
	direction: SortDirection | undefined;
};
type PropType = {
	onChangeJql?: (jql: string) => void;
	jql: string;
	JqlUtilsInstance: JqlUtilsType | undefined;
};
type ContainerProps = {
	children: ReactNode;
	jql: string;
	onChangeJql?: (jql: string) => void;
};
type ActionType = {
	setFieldAndDirection: (
		field: string,
		direction: SortDirection | undefined,
		aliases: string[],
	) => Action<StoreType, PropType>;
	// setDirection: (direction: SortDirection) => Action<StoreType, PropType>,
	setField: (field: string, aliases: string[]) => Action<StoreType, PropType>;
};

const Store = createStore<StoreType, ActionType>({
	initialState: { field: 'created', direction: 'DESC' },
	actions: {
		setField:
			(field, aliases) =>
			({ getState, setState }, { onChangeJql, jql, JqlUtilsInstance }) => {
				const { field: prevField } = getState();
				if (field !== prevField) {
					setState({ field });
					const newJql = JqlUtilsInstance?.jql(jql)
						.setPrimarySortField(field, undefined, aliases)
						.toJql();
					typeof newJql === 'string' && onChangeJql && onChangeJql(newJql);
				}
			},
		setFieldAndDirection:
			(field, direction, aliases) =>
			({ getState, setState }, { onChangeJql, jql, JqlUtilsInstance }) => {
				const { field: prevField, direction: prevDirection } = getState();
				if (field !== prevField || direction !== prevDirection) {
					setState({ field, direction });
					if (direction !== 'NONE' || fg('jsc_list_reranking_gate')) {
						const newJql = JqlUtilsInstance?.jql(jql)
							.setPrimarySortField(field, direction, aliases)
							.toJql();
						typeof newJql === 'string' && onChangeJql && onChangeJql(newJql);
					}
				}
			},
	},
	name: 'IssueNavigatorSelectedViewState',
});

export const setFieldAndDirectionInState =
	(): Action<StoreType, PropType> =>
	({ getState, setState }, { jql, JqlUtilsInstance }) => {
		const field = JqlUtilsInstance?.jql(jql).getPrimarySortField();
		const direction = JqlUtilsInstance?.jql(jql).getPrimarySortDirection();
		const { field: prevField, direction: prevDirection } = getState();
		if (field !== prevField || direction !== prevDirection) {
			setState({ field, direction });
		}
	};

export const Container = createContainer<StoreType, ActionType, PropType>(Store, {
	onInit: setFieldAndDirectionInState,
	onUpdate: setFieldAndDirectionInState,
});

const useJqlUtils = importCond<
	typeof import('@atlassian/jira-jql-utils/src/main.tsx'),
	typeof import('@atlassian/jira-issue-navigator/src/controllers/use-jql-utils/index.tsx')
>(
	'load_jql_utils_directly',
	'@atlassian/jira-jql-utils/src/main.tsx',
	'@atlassian/jira-issue-navigator/src/controllers/use-jql-utils/index.tsx',
);

export const SortContainer = (props: ContainerProps) => {
	const Utils = useJqlUtils();

	return <Container JqlUtilsInstance={Utils} {...props} />;
};

export const useSortFieldAndDirection = createHook(Store, {
	selector: ({ field, direction }) => ({ field, direction }),
});
